import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import DOMPurify from 'dompurify';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  FileText, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Pen,
  Clock,
  Shield,
  ChevronLeft,
  RefreshCw,
  Download,
  Eye
} from 'lucide-react';

// Simple Signature Pad Component
const SignaturePad = ({ onSave, onClear }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    setIsDrawing(true);
    setHasSignature(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onClear && onClear();
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL('image/png');
    onSave && onSave(dataUrl);
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-muted-foreground/30 rounded-lg p-2 bg-white">
        <canvas
          ref={canvasRef}
          width={500}
          height={150}
          className="w-full cursor-crosshair touch-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex justify-between">
        <Button variant="outline" size="sm" onClick={clearSignature}>
          <RefreshCw className="h-3 w-3 mr-1" />
          Limpiar
        </Button>
        <Button 
          size="sm" 
          onClick={saveSignature} 
          disabled={!hasSignature}
          className="btn-primary"
        >
          <Pen className="h-3 w-3 mr-1" />
          Confirmar Firma
        </Button>
      </div>
    </div>
  );
};

const SignatureRequestPage = () => {
  const { requestId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Signing flow state
  const [step, setStep] = useState(1); // 1: Read, 2: Consent, 3: Sign, 4: Confirm
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [consentCheckbox, setConsentCheckbox] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [confirmRead, setConfirmRead] = useState(false);
  const [signing, setSigning] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  const documentRef = useRef(null);

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const response = await api.get(`/signature-requests/${requestId}`);
      setRequest(response.data);
      
      // Set initial step based on status
      if (response.data.status === 'signed' || response.data.status === 'completed') {
        setStep(4);
      } else if (response.data.electronic_consent_given) {
        setConsentGiven(true);
        setStep(3);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar el documento');
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setHasScrolledToBottom(true);
    }
  };

  const handleGiveConsent = async () => {
    try {
      await api.post(`/signature-requests/${requestId}/consent`, {
        consent_given: true,
        consent_statement: "Acepto utilizar firma electrónica para la ejecución de documentos en esta plataforma."
      });
      setConsentGiven(true);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrar el consentimiento');
    }
  };

  const handleSign = async () => {
    if (!signatureData || !confirmRead) return;
    
    setSigning(true);
    try {
      const response = await api.post(`/signature-requests/${requestId}/sign`, {
        signature_data: signatureData,
        confirmation_checkbox: true
      });
      
      setRequest(prev => ({
        ...prev,
        status: 'signed',
        signed_at: response.data.signed_at
      }));
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al firmar el documento');
    } finally {
      setSigning(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    
    setRejecting(true);
    try {
      await api.post(`/signature-requests/${requestId}/reject`, {
        reason: rejectReason
      });
      navigate('/app/signatures', { state: { message: 'Documento rechazado' } });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al rechazar el documento');
    } finally {
      setRejecting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { className: 'bg-amber-500/10 text-amber-500 border-amber-500/20', label: 'Pendiente', icon: Clock },
      viewed: { className: 'bg-blue-500/10 text-blue-500 border-blue-500/20', label: 'Visto', icon: Eye },
      signed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Firmado', icon: CheckCircle2 },
      completed: { className: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', label: 'Completado', icon: CheckCircle2 },
      rejected: { className: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Rechazado', icon: XCircle },
      expired: { className: 'bg-muted text-muted-foreground border-muted', label: 'Expirado', icon: Clock },
      cancelled: { className: 'bg-muted text-muted-foreground border-muted', label: 'Cancelado', icon: XCircle },
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={`${style.className} gap-1`}>
        <Icon className="h-3 w-3" />
        {style.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <Alert className="border-red-500/20 bg-red-500/5">
          <AlertCircle className="h-4 w-4 text-red-500" />
          <AlertDescription className="text-red-500">{error}</AlertDescription>
        </Alert>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/app/signatures')}>
          <ChevronLeft className="h-4 w-4 mr-2" />
          Volver a Documentos
        </Button>
      </div>
    );
  }

  if (!request) return null;

  const isActionable = ['pending', 'viewed'].includes(request.status);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/app/signatures" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ChevronLeft className="h-4 w-4" />
          Mis Documentos
        </Link>
        {getStatusBadge(request.status)}
      </div>

      {/* Document Info */}
      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">{request.template_name}</CardTitle>
              <CardDescription className="mt-1">
                Referencia: {request.document_reference}
              </CardDescription>
            </div>
            <FileText className="h-8 w-8 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Proyecto</p>
              <p className="font-medium">{request.opportunity_title || 'N/A'}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Fecha de Creación</p>
              <p className="font-medium">{new Date(request.created_at).toLocaleDateString('es-MX')}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Expira</p>
              <p className="font-medium">{request.expires_at ? new Date(request.expires_at).toLocaleDateString('es-MX') : 'N/A'}</p>
            </div>
            {request.signed_at && (
              <div>
                <p className="text-muted-foreground">Firmado</p>
                <p className="font-medium">{new Date(request.signed_at).toLocaleDateString('es-MX')}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Progress Steps */}
      {isActionable && (
        <div className="flex items-center justify-center gap-2">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
              }`}>
                {s}
              </div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-primary' : 'bg-muted'}`} />}
            </div>
          ))}
        </div>
      )}

      {/* Step 1: Read Document */}
      {step === 1 && isActionable && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Paso 1: Leer el Documento
            </CardTitle>
            <CardDescription>
              Por favor lea el documento completo antes de continuar. Desplácese hasta el final para habilitar el siguiente paso.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              ref={documentRef}
              onScroll={handleScroll}
              className="max-h-96 overflow-y-auto border border-border rounded-lg bg-white p-6"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(request.document_content || '') }}
            />
            
            <Alert className={hasScrolledToBottom ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-amber-500/20 bg-amber-500/5'}>
              <AlertCircle className={`h-4 w-4 ${hasScrolledToBottom ? 'text-emerald-500' : 'text-amber-500'}`} />
              <AlertDescription className={hasScrolledToBottom ? 'text-emerald-600' : 'text-amber-600'}>
                {hasScrolledToBottom 
                  ? 'Ha leído el documento completo. Puede continuar al siguiente paso.'
                  : 'Desplácese hasta el final del documento para continuar.'}
              </AlertDescription>
            </Alert>

            <div className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => setShowRejectDialog(true)}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
              >
                Rechazar Documento
              </Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!hasScrolledToBottom}
                className="btn-primary"
              >
                Continuar al Consentimiento
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Electronic Consent */}
      {step === 2 && isActionable && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Paso 2: Consentimiento de Firma Electrónica
            </CardTitle>
            <CardDescription>
              Antes de firmar, debe aceptar el uso de firma electrónica para este documento.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-500/20 bg-blue-500/5">
              <Shield className="h-4 w-4 text-blue-500" />
              <AlertDescription className="text-blue-600">
                <p className="font-medium mb-2">Aviso Legal sobre Firma Electrónica</p>
                <p className="text-sm">
                  Al aceptar este consentimiento, usted reconoce y acepta que:
                </p>
                <ul className="text-sm list-disc ml-4 mt-2 space-y-1">
                  <li>Su firma electrónica tendrá el mismo efecto legal que una firma manuscrita para los efectos de este documento.</li>
                  <li>Esta firma electrónica constituye una expresión de intención válida y vinculante.</li>
                  <li>Se registrará su dirección IP, fecha y hora como parte del registro de auditoría.</li>
                  <li>Este documento está sujeto a la formalización de documentos legales adicionales según corresponda.</li>
                </ul>
              </AlertDescription>
            </Alert>

            <div className="flex items-start space-x-3 p-4 border border-border rounded-lg">
              <Checkbox 
                id="consent"
                checked={consentCheckbox}
                onCheckedChange={setConsentCheckbox}
              />
              <label htmlFor="consent" className="text-sm leading-relaxed cursor-pointer">
                <span className="font-medium">Acepto utilizar firma electrónica</span> para la ejecución de documentos en esta plataforma. 
                Entiendo que mi firma electrónica tendrá validez legal y que se registrarán mis datos de identificación 
                (IP, fecha, hora) como parte del proceso de firma.
              </label>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Volver
              </Button>
              <Button 
                onClick={handleGiveConsent} 
                disabled={!consentCheckbox}
                className="btn-primary"
              >
                Aceptar y Continuar a Firma
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Sign */}
      {step === 3 && isActionable && (
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Pen className="h-5 w-5" />
              Paso 3: Firmar el Documento
            </CardTitle>
            <CardDescription>
              Dibuje su firma en el área de abajo y confirme que ha leído y acepta el documento.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Signature Pad */}
            <div>
              <p className="text-sm font-medium mb-2">Su Firma</p>
              <SignaturePad 
                onSave={setSignatureData} 
                onClear={() => setSignatureData(null)}
              />
              {signatureData && (
                <p className="text-xs text-emerald-500 mt-2 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Firma capturada correctamente
                </p>
              )}
            </div>

            <Separator />

            {/* Confirmation */}
            <div className="flex items-start space-x-3">
              <Checkbox 
                id="confirmRead"
                checked={confirmRead}
                onCheckedChange={setConfirmRead}
              />
              <label htmlFor="confirmRead" className="text-sm leading-relaxed cursor-pointer">
                <span className="font-medium">Confirmo que he leído y entendido</span> el documento completo, 
                incluyendo todos los términos, condiciones y riesgos asociados. Entiendo que al firmar, 
                estoy expresando mi acuerdo con el contenido del documento.
              </label>
            </div>

            <Alert className="border-amber-500/20 bg-amber-500/5">
              <AlertCircle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-600 text-sm">
                Una vez firmado, este documento no podrá ser modificado. Si tiene dudas, consulte con sus asesores antes de firmar.
              </AlertDescription>
            </Alert>

            <div className="flex justify-between">
              <Button 
                variant="outline" 
                onClick={() => setShowRejectDialog(true)}
                className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
              >
                Rechazar Documento
              </Button>
              <Button 
                onClick={handleSign} 
                disabled={!signatureData || !confirmRead || signing}
                className="btn-primary"
              >
                {signing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Firmando...
                  </>
                ) : (
                  <>
                    <Pen className="h-4 w-4 mr-2" />
                    Firmar Documento
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirmation / Completed */}
      {(step === 4 || ['signed', 'completed'].includes(request.status)) && (
        <Card className="card-dashboard border-emerald-500/20">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-semibold">Documento Firmado Exitosamente</h2>
            <p className="text-muted-foreground">
              Su firma ha sido registrada y el documento ha sido procesado correctamente.
            </p>
            <div className="bg-muted/50 rounded-lg p-4 inline-block">
              <p className="text-sm text-muted-foreground">Referencia del Documento</p>
              <p className="font-mono font-medium">{request.document_reference}</p>
            </div>
            {request.signed_at && (
              <p className="text-sm text-muted-foreground">
                Firmado el {new Date(request.signed_at).toLocaleString('es-MX')}
              </p>
            )}
            <div className="flex justify-center gap-4 pt-4">
              <Button variant="outline" asChild>
                <Link to="/app/signatures">Ver Todos los Documentos</Link>
              </Button>
              <Button className="btn-primary" asChild>
                <Link to="/app/dashboard">Ir al Dashboard</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rejected/Expired/Cancelled States */}
      {['rejected', 'expired', 'cancelled'].includes(request.status) && (
        <Card className="card-dashboard border-red-500/20">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-500/10 mx-auto flex items-center justify-center">
              <XCircle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold">
              {request.status === 'rejected' && 'Documento Rechazado'}
              {request.status === 'expired' && 'Documento Expirado'}
              {request.status === 'cancelled' && 'Documento Cancelado'}
            </h2>
            {request.rejection_reason && (
              <p className="text-muted-foreground">{request.rejection_reason}</p>
            )}
            <Button variant="outline" asChild className="mt-4">
              <Link to="/app/signatures">Volver a Documentos</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Documento</DialogTitle>
            <DialogDescription>
              Por favor indique el motivo por el cual rechaza firmar este documento.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Escriba el motivo del rechazo..."
              className="w-full h-24 p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={!rejectReason.trim() || rejecting}
            >
              {rejecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Rechazando...
                </>
              ) : (
                'Confirmar Rechazo'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignatureRequestPage;
